import { Suspense, lazy } from "react";
import { IconBolt, IconCalendar } from "@tabler/icons-react";
import { AccountingSoftwareSettings } from "../AccountingSoftwareSettings";
import { FilesUploadHistorySection } from "./FilesUploadHistorySection";
import { StatementCoverageSection } from "./StatementCoverageSection";
import { InvoiceRefreshCard } from "./InvoiceRefreshCard";
import { useAuth } from "../../hooks/useAuth";
import { useAsyncAccountingRefresh } from "../../hooks/useAsyncAccountingRefresh";
import { TrialScope } from "../auth/TrialScope";
import {
  IntegrationConnectButton,
  IntegrationOutlineButton,
} from "../integrations/IntegrationButtons";

const AgingUploadZone = lazy(() =>
  import("@/components/transactions/AgingUploadZone").then((m) => ({
    default: m.AgingUploadZone,
  })),
);

function UploadFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center rounded-[10px] border border-dashed border-slate-200 bg-slate-50">
      <p className="text-sm text-slate-500">Loading uploader…</p>
    </div>
  );
}

function CashflowUpsell() {
  return (
    <div className="rounded-[10px] border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
      <p className="text-sm font-medium text-slate-700">
        Cashflow access required
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Upload bank transactions and aging reports from the Cashflow workspace,
        or ask your admin to enable the Cashflow scope for your role.
      </p>
    </div>
  );
}

export default function FilesIntegrationsPage() {
  const { scopes } = useAuth();
  const hasCashflow = scopes?.includes("CASHFLOW") ?? false;
  const { organization, refreshInvoicesMutation, refreshCustomersMutation, isRefreshing, startRefresh } =
    useAsyncAccountingRefresh();
  const hasAccountingSoftware = organization?.accounting_software != null;

  const scrollToIntegrations = () => {
    document
      .getElementById("available-integrations")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex min-h-full w-full max-w-7xl flex-col">
      <div className="mb-6 shrink-0">
        <h1 className="text-3xl font-bold tracking-tight text-[#0f172b]">
          Files & Integrations
        </h1>
        <p className="mt-1 text-base text-[#45556c]">
          Upload files manually or connect your accounting software
        </p>
      </div>

      <TrialScope>
        <div className="mb-6 rounded-[14px] border border-[#bedbff] bg-linear-to-r from-[#eff6ff] to-[#eef2ff] p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#155dfc] text-white shadow-sm">
                <IconBolt size={24} stroke={2} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0f172b]">
                  Stop uploading, start automating.
                </h2>
                <p className="mt-1 text-sm text-[#45556c]">
                  Connect QuickBooks, Xero, or Stripe to get real-time cashflow
                  updates without the Excel hassle.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2 self-start lg:self-center">
              <IntegrationConnectButton onClick={scrollToIntegrations}>
                Connect Integration
              </IntegrationConnectButton>
              <IntegrationOutlineButton to="/pricing">
                View pricing
              </IntegrationOutlineButton>
            </div>
          </div>
        </div>
      </TrialScope>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-6">
          {/* Bank transactions are uploaded per account in Statement
              coverage below, so the statement is tied to the account it
              belongs to rather than landing in an unnamed batch. */}

          <article className="overflow-hidden rounded-[14px] border border-black/10 bg-white">
            <header className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-base font-medium text-[#0a0a0a]">
                Upload invoices
              </h2>
              <p className="mt-1 text-sm text-[#45556c]">
                Track receivables aging
              </p>
            </header>
            <div className="px-6 pb-6 pt-2">
              {hasCashflow ? (
                <Suspense fallback={<UploadFallback />}>
                  <AgingUploadZone />
                </Suspense>
              ) : (
                <CashflowUpsell />
              )}
              <div className="mt-4 flex gap-3 rounded-[10px] bg-[#eff6ff] px-3 py-3">
                <IconCalendar
                  size={20}
                  className="mt-0.5 shrink-0 text-[#1447e6]"
                  stroke={1.75}
                />
                <div>
                  <p className="text-sm font-medium text-[#1c398e]">
                    Expected columns:
                  </p>
                  <p className="text-xs text-[#1447e6]">
                    Due Date, Amount, Email (recommended: Invoice Number,
                    Customer name / company column)
                  </p>
                </div>
              </div>
            </div>
          </article>

          {hasAccountingSoftware && (
            <InvoiceRefreshCard
              organization={organization}
              refreshInvoicesMutation={refreshInvoicesMutation}
              refreshCustomersMutation={refreshCustomersMutation}
              isRefreshing={isRefreshing}
              onRefreshInvoices={() => startRefresh()}
              onRefreshCustomers={() => startRefresh({ customersOnly: true })}
            />
          )}
        </div>

        <div
          id="available-integrations"
          className="flex scroll-mt-6 flex-col gap-6"
        >
          <article className="overflow-hidden rounded-[14px] border border-black/10 bg-white">
            <header className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-base font-medium text-[#0a0a0a]">
                Available Integrations
              </h2>
              <p className="mt-1 text-sm text-[#45556c]">
                Connect your accounting software for automatic syncing
              </p>
            </header>
            <div className="px-6 pb-6 pt-4">
              <AccountingSoftwareSettings />
            </div>
          </article>
        </div>
      </div>

      {hasCashflow && (
        <div className="mt-8">
          <StatementCoverageSection />
        </div>
      )}

      {hasCashflow && <FilesUploadHistorySection />}
    </div>
  );
}
